import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import map from 'lodash/map';
import find from 'lodash/find';
import { filter } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Col } from 'react-bootstrap';
import HealthIcon from '../../TrendKPIList/Sections/HealthIcon';
import { KPI_BUTTONS, MAX_SELECTED_KPIS } from '../../TrendKPIList/Sections/constants';
import clsx from 'clsx';
import { addTrendKpi, removeSelectedKpi } from '../../actions';
import { Checkbox } from 'sharedComponents/Checkbox';
import { SearchBox } from 'sharedComponents/SearchBox';
import { IconCondition } from 'svgIcons/MotionPortalIcons';
import { contractInfoMessages, limitIndications, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import useUserCapability from 'sharedHooks/useUserCapability';
import { setIsDefaultLimitsSelected } from '../../LimitSelector/actions';
import { GeneralButton } from 'sharedComponents/GeneralButton';

const renderMinMaxAndAverage = (minValue, maxValue, avgValue, offset, hasLengthSelectedTrendKpi, precision) => {
    const min = minValue || minValue === 0 ? +minValue + +offset : '';
    const max = maxValue || maxValue === 0 ? +maxValue + +offset : '';
    const avg = avgValue || avgValue === 0 ? +avgValue + +offset : '-';

    // Extract nested ternary for min/max separator
    const minMaxSeparator = minValue !== null && maxValue !== null ? '/' : '-';

    return <>
        <div className='data-min-max'>
            <span>{hasLengthSelectedTrendKpi ? min !== '' && min?.toFixed(precision) : ''}</span>
            <span>{hasLengthSelectedTrendKpi ? minMaxSeparator : ''}</span>
            <span>{hasLengthSelectedTrendKpi ? max !== '' && max?.toFixed(precision) : ''}</span>
        </div>
        <div className='data-average'>
            <span>{hasLengthSelectedTrendKpi ? avg !== '-' && avg?.toFixed(precision) : ''}</span>
        </div>
    </>;
};

const renderKpiItem = (kpi, isSelected, selectedTrendKpi, userCapabilities, toggleMeasurement, capability, dateSelection, setShowLimitSetting, setClickedConfigKpi) => {
    const hasLengthSelectedTrendKpi = selectedTrendKpi?.length;
    const minValue = hasLengthSelectedTrendKpi ? selectedTrendKpi[0]?.minValue : undefined;
    const maxValue = hasLengthSelectedTrendKpi ? selectedTrendKpi[0]?.maxValue : undefined;
    const avgValue = hasLengthSelectedTrendKpi ? selectedTrendKpi[0]?.avgValue : undefined;
    const offset = selectedTrendKpi?.[0]?.offset ?? 0;
    const precision = selectedTrendKpi?.[0]?.precision ?? 1;

    const handleToggle = () => {
        if (kpi?.isPlottableToTrendChart) {
            toggleMeasurement(kpi, isSelected);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggle();
        }
    };

    return (
        <div
            className={clsx('measurement-list-item', isSelected && 'selected-measurement-list-item')}
            id='measurement-list'
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            role='button'
            tabIndex={kpi?.isPlottableToTrendChart ? 0 : -1}
            key={kpi.timeseriesKey}
        >
            <div className='data-command-checkbox' id='data-command'>
                <Checkbox
                    checked={isSelected}
                    disabled={!kpi?.isPlottableToTrendChart}
                />
            </div>
            <div className='data-label' id='data-label'>
                <div className='health-icon' id='health-icon'>
                    <HealthIcon kpi={kpi} />
                </div>
                <div className='condition-icon-label' id='condition-icon-label'>
                    {userCapabilities?.hasOperationalParametersLimitStatus() &&
                        <div className='condition-icon' id='condition-icon'>
                            {kpi?.limitStatus && <IconCondition condition={limitIndications[kpi.limitStatus]?.iconID} />}
                        </div>
                    }
                    <div>{kpi.label}</div>
                </div>
                {kpi?.unit && <div className='data-label__unit' id='data-label__unit'>{kpi?.unit}</div>}
            </div>
            {userCapabilities?.hasOperationalParametersMinMax() && renderMinMaxAndAverage(minValue, maxValue, avgValue, offset, hasLengthSelectedTrendKpi, precision)}
            <div className='data-value' id='data-value'>{kpi.displayValue ?? '-'}</div>
            <div className='data-command' id='data-command'>
                {map(KPI_BUTTONS, (KpiButton, index) =>
                    <KpiButton
                        kpi={kpi}
                        capability={capability}
                        isSelected={isSelected}
                        dateSelection={dateSelection}
                        key={index}
                        setShowLimitSetting={setShowLimitSetting}
                        setClickedConfigKpi={setClickedConfigKpi}
                    />
                )}
            </div>
        </div>
    );
};

const EnergyParameterSection = (props) => {
    const { t: translate } = useTranslation();
    const { section, capability, selectedTrendKPIs, dateSelection, selectedTrendsData, setShowLimitSetting, setClickedConfigKpi, selectedNotification } = props;
    const userCapabilities = useUserCapability();
    const hasActiveSubscriptionAndCapability = capability?.subscriptionStatus() === contractInfoMessages?.activeSubscription && capability?.hasLimitConfigCapability();
    const [searchboxFilter, setSearchboxFilter] = useState('');
    const dispatch = useDispatch();

    // Treat missing section/data as empty array to ensure hooks run in stable order
    const data = section?.data || [];
    let kpiData = data;
    const searchBoxValue = searchboxFilter.toLowerCase();
    if (searchBoxValue !== '' && searchBoxValue !== null) {
        kpiData = data?.filter((kpi) => kpi?.label?.toLowerCase().includes(searchBoxValue));
    }

    const toggleMeasurement = (kpi, isSelected) => {
        if (isSelected) {
            dispatch(removeSelectedKpi(kpi));
        } else {
            dispatch(addTrendKpi(kpi));
        }
        dispatch(setIsDefaultLimitsSelected(false));
    };

    const handleSearchChange = (searchTerm) => {
        if (searchTerm.length <= 50) {
            setSearchboxFilter(searchTerm);
        }
    };

    // Ensure selectedTrendKPIs is always an array
    const safeSelectedTrendKPIs = Array.isArray(selectedTrendKPIs) ? selectedTrendKPIs : [];
    const { selectedKpiItems, unselectedKpiItems } = kpiData.reduce((acc, kpi) => {
        if (kpi.timeseriesKey) {
            if (safeSelectedTrendKPIs.includes(kpi.timeseriesKey)) {
                acc.selectedKpiItems.push(kpi);
            } else {
                acc.unselectedKpiItems.push(kpi);
            }
        }
        return acc;
    }, { selectedKpiItems: [], unselectedKpiItems: [] });

    // Combine selected and unselected KPI items to maintain the desired order for rendering
    const sortedKpiData = [...selectedKpiItems, ...unselectedKpiItems];

    // SET TREND KPI FROM NOTIFICATION
    useEffect(() => {
        if (selectedNotification && kpiData?.length && selectedNotification?.properties?.timeseriesKey) {
            const kpi = selectedNotification?.properties;
            dispatch(addTrendKpi(kpi));
        }
    }, [selectedNotification, kpiData]);

    return <Col xl lg={12} className={clsx('measurement-list', !userCapabilities?.hasOperationalParametersMinMax() && 'measurement-list-capability')}>
        <div className='measurement-list-title' id='measurement-list-title'>
            <div className='signal-group-header-info' id='signal-group-header-info'>
                <div className='kpisignal__filter__search me-2' id='kpisignal__filter__search'>
                    <SearchBox
                        placeholder={translate('ABB.Powertrain.Frontend.searchSignal')}
                        onChange={handleSearchChange}
                        value={searchboxFilter}
                        maxLength={MAX_INPUT_CHAR_LENGTH} />
                </div>
                <span>{Array.isArray(selectedTrendKPIs) ? selectedTrendKPIs.length : 0}/{MAX_SELECTED_KPIS}{' '}{translate('ABB.Powertrain.Frontend.selected')}</span>
            </div>
            {hasActiveSubscriptionAndCapability ? <div className='limit-settings-button-wrapper'>
                <div className='kpisignal__filter__search' id='kpisignal__filter__search'>
                    <GeneralButton
                        type='normal'
                        className='limit-config-Cancel-button'
                        text={translate('ABB.Powertrain.Frontend.limitsSetting')}
                        onClick={() => { setShowLimitSetting(true); }}
                    />
                </div>
            </div> : null}
        </div>
        <div className='measurement-list-head' id='measurement-list-head'>
            <div className='measurement-list-item'>
                <div className='data-command-checkbox' id='data-command-checkbox'></div>
                <div className='data-label'>{translate('ABB.Powertrain.Frontend.operationalParametersNameLabel')}</div>
                {userCapabilities?.hasOperationalParametersMinMax() && <>
                    <div className='data-min-max' id='data-min-max'>
                        {translate('ABB.Powertrain.Frontend.operationalParametersMinLabel')}/{translate('ABB.Powertrain.Frontend.operationalParametersMaxLabel')}
                    </div>
                    <div className='data-average' id='data-average'>
                        {translate('ABB.Powertrain.Frontend.operationalParametersAverageLabel')}
                    </div>
                </>}
                <div className='data-value' id='data-value'>{translate('ABB.Powertrain.Frontend.opertaionalParametersLastMeasurementLabel')}</div>
                <div className='data-command' id='data-command'></div>
            </div>
        </div>
        <div className='measurement-list-body' id='measurement-list-body'>
            {map(sortedKpiData, (kpi) => {
                const isSelected = !!find(selectedTrendKPIs, (selectedTrendKPI) => kpi.timeseriesKey === selectedTrendKPI);
                const selectedTrendKpi = filter(selectedTrendsData, (selectedTrendData) => selectedTrendData?.kpiIdentifier === kpi?.timeseriesKey);
                return renderKpiItem(kpi, isSelected, selectedTrendKpi, userCapabilities, toggleMeasurement, capability, dateSelection, setShowLimitSetting, setClickedConfigKpi);
            })}
        </div>
    </Col>;
};

EnergyParameterSection.propTypes = {
    section: PropTypes.shape({
        data: PropTypes.array,
        sectionId: PropTypes.any
    }),
    capability: PropTypes.object,
    selectedTrendKPIs: PropTypes.array,
    selectedTrendsData: PropTypes.array,
    dateSelection: PropTypes.object,
    setShowLimitSetting: PropTypes.func,
    setClickedConfigKpi: PropTypes.func,
    selectedNotification: PropTypes.shape({
        properties: PropTypes.object,
    }),
};

export default EnergyParameterSection;
